import { DEFAULT_ZONE_KIND_COLOR, ZONE_KIND_COLOR_KEYS, ZONE_KIND_COLORS, type ZoneKindInput } from '@chukua/common';
import { Alert, Button, Drawer, Form, Input, Select } from 'antd';
import { useEffect } from 'react';
import { ZoneKindSwatch } from '../../../../../shared/components/ZoneKindSwatch';
import type { ZoneKind } from '../../../reference-data.api';

const COLOR_OPTIONS = ZONE_KIND_COLOR_KEYS.map((color) => ({
  value: color,
  label: (
    <span className="flex items-center gap-2">
      <ZoneKindSwatch color={color} />
      {ZONE_KIND_COLORS[color].label}
    </span>
  ),
}));

interface ZoneKindFormDrawerProps {
  open: boolean;
  /** Present to edit that row; absent to create a new one. */
  editing?: ZoneKind;
  error: string | null;
  submitting: boolean;
  onSubmit: (input: ZoneKindInput) => Promise<boolean>;
  onClose: () => void;
}

/** One drawer for both create and edit — same shape as TrailerTypeFormDrawer. */
export function ZoneKindFormDrawer({ open, editing, error, submitting, onSubmit, onClose }: ZoneKindFormDrawerProps) {
  const [form] = Form.useForm<ZoneKindInput>();

  useEffect(() => {
    if (open) form.setFieldsValue(editing ?? { code: '', name: '', color: DEFAULT_ZONE_KIND_COLOR });
  }, [open, editing, form]);

  async function submit(values: ZoneKindInput) {
    const saved = await onSubmit(values);
    if (saved) onClose();
  }

  return (
    <Drawer open={open} onClose={onClose} title={editing ? 'Edit zone kind' : 'New zone kind'} placement="right" size={420} destroyOnClose>
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form<ZoneKindInput> form={form} layout="vertical" requiredMark={false} onFinish={submit} disabled={submitting}>
        <Form.Item name="code" label="Code" rules={[{ required: true, message: 'Enter a code' }]}>
          <Input className="font-mono" autoFocus />
        </Form.Item>
        <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Enter a name' }]}>
          <Input />
        </Form.Item>
        <Form.Item
          name="color"
          label="Map colour"
          extra="How this kind's zones are drawn on the Zones map."
          rules={[{ required: true, message: 'Choose a colour' }]}
        >
          <Select options={COLOR_OPTIONS} />
        </Form.Item>
        <Form.Item className="mb-0 pt-2">
          <Button type="primary" htmlType="submit" loading={submitting}>
            {editing ? 'Save' : 'Create zone kind'}
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}
